import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as noteTypesApi from "@/lib/api/noteTypes";
import * as notesApi from "@/lib/api/notes";
import { queryKeys } from "@/lib/query-keys";

export type { NoteTypeOption } from "@/lib/api/noteTypes";

interface GenerateTodoInstructionsResponse {
  instructions: string;
}

export function useNoteTypesQuery() {
  return useQuery(noteTypesQueryOptions());
}

export function noteTypesQueryOptions() {
  return {
    queryKey: queryKeys.noteTypes.all,
    queryFn: async () => {
      return noteTypesApi.fetchNoteTypes();
    },
  };
}

export function useCreateNoteTypeMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      name: string;
      label: string;
      icon?: string;
      color?: string;
      description?: string;
      promptInstructions?: string;
    }) => {
      return noteTypesApi.createNoteType(data);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.noteTypes.all,
      });
    },
  });
}

export function useUpdateNoteTypeMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...data
    }: {
      id: string;
      label?: string;
      icon?: string;
      color?: string;
      description?: string;
      promptInstructions?: string;
    }) => {
      return noteTypesApi.updateNoteType(id, data);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.noteTypes.all,
      });
    },
  });
}

export function useDeleteNoteTypeMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return noteTypesApi.deleteNoteType(id);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.noteTypes.all,
      });
    },
  });
}

export function useDetectNoteTypeMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["detectNoteType"],
    mutationFn: async ({
      noteId,
      noteContent,
      noteTitle,
    }: {
      noteId: string;
      noteContent?: string;
      noteTitle?: string;
    }) => {
      const result = await noteTypesApi.detectNoteType(noteId);
      return { ...result, noteContent, noteTitle };
    },
    onSuccess: async (data) => {
      console.log("detectNoteType success!", data);
      console.log("noteContent for instructions:", data.noteContent);
      console.log("noteTitle for instructions:", data.noteTitle);

      if (
        (data.noteContent || data.noteTitle) &&
        data.detection?.suggestedType === "watch"
      ) {
        try {
          const textForInstructions = (data.noteContent || data.noteTitle)!;
          console.log(
            "Auto-generating instructions for watch item:",
            textForInstructions,
          );
          const instructionsData = (await notesApi.generateTodoInstructions({
            todoText: textForInstructions,
            noteId: data.noteId,
          })) as GenerateTodoInstructionsResponse;

          console.log(
            "Generated and stored watch instructions:",
            instructionsData.instructions,
          );
        } catch (error) {
          console.error("Error generating watch instructions:", error);
        }
      }

      void queryClient.invalidateQueries({
        predicate: (query) => {
          if (query.queryKey[0] === "todos") return true;
          if (query.queryKey[0] === "notes") return true;
          if (query.queryKey[0] === "note") return true;
          if (query.queryKey[0] === "todo") return true;
          if (query.queryKey[0] === "noteLists") return true;
          return false;
        },
      });
    },
    onError: (error) => {
      console.error("Error detecting note type:", error);
    },
  });
}
